import React, { useMemo, useState, useRef } from "react";
import UserAvatar from "./UserAvatar";

/**
 * Search-and-select users. Controlled: `selected` is an array of user objects.
 * `users` is the full candidate list; already-selected users are hidden by id.
 */
function UserPicker({ users, selected, onAdd, onRemove, disabled, placeholder = "Search people by name or email…", loading }) {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  const selectedIds = useMemo(() => new Set(selected.map((u) => u.id)), [selected]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return users
      .filter((u) => !selectedIds.has(u.id))
      .filter((u) => `${u.display_name || ""} ${u.email_address || ""}`.toLowerCase().includes(q))
      .slice(0, 30);
  }, [query, users, selectedIds]);

  const pick = (user) => {
    if (!user) return;
    onAdd(user);
    setQuery("");
    setActive(0);
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown" && matches.length) {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, matches.length - 1));
    } else if (e.key === "ArrowUp" && matches.length) {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault(); // never submit the surrounding form
      if (matches[active]) pick(matches[active]);
    } else if (e.key === "Escape" && query) {
      e.stopPropagation(); // clear the query before closing the modal
      setQuery("");
    } else if (e.key === "Backspace" && !query && selected.length) {
      onRemove(selected[selected.length - 1]);
    }
  };

  return (
    <div className='bh-picker'>
      {selected.length > 0 && (
        <ul className='bh-chips'>
          {selected.map((u) => (
            <li key={u.id} className='bh-chip'>
              <UserAvatar user={u} size={22} />
              <span className='bh-chip__name'>{u.display_name}</span>
              <button
                type='button'
                className='bh-chip__remove'
                onClick={() => onRemove(u)}
                disabled={disabled}
                aria-label={`Remove ${u.display_name}`}
              >
                <i className='fa fa-times' aria-hidden='true'></i>
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className='bh-search'>
        <i className='fa fa-search bh-search__icon' aria-hidden='true'></i>
        <input
          ref={inputRef}
          type='text'
          className='bh-input bh-search__input'
          value={query}
          disabled={disabled}
          placeholder={loading ? "Loading people…" : placeholder}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
          role='combobox'
          aria-expanded={matches.length > 0}
          aria-controls='bh-picker-listbox'
          aria-autocomplete='list'
        />
      </div>
      {query.trim() && (
        <ul className='bh-picker__list' role='listbox' id='bh-picker-listbox'>
          {matches.length === 0 ? (
            <li className='bh-picker__none'>No people match “{query.trim()}”</li>
          ) : (
            matches.map((u, i) => (
              <li
                key={u.id}
                role='option'
                aria-selected={i === active}
                className={`bh-picker__item ${i === active ? "is-active" : ""}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(u)}
              >
                <UserAvatar user={u} size={32} />
                <span className='bh-picker__text'>
                  <span className='bh-picker__name'>{u.display_name}</span>
                  {u.email_address && <span className='bh-picker__sub'>{u.email_address}</span>}
                </span>
                <i className='fa fa-plus bh-picker__add' aria-hidden='true'></i>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

export default UserPicker;
